import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRouterLink,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react'
import { closeOutline } from 'ionicons/icons'
import debounce from 'lodash/debounce'
import { useElementSize } from 'usehooks-ts'

import { Avatar } from '@/components/atoms/Avatar'
import { BackButton } from '@/components/atoms/BackButton'
import { Button } from '@/components/atoms/Button'
import { CharacterCount } from '@/components/atoms/CharacterCount'
import { Title } from '@/components/atoms/Title'
import { UserName } from '@/components/atoms/UserName'
import { UserRelationIndicator } from '@/components/atoms/UserRelationIndicator'
import { AddImageButton } from '@/components/molecules/AddImageButton'
import { MentionsInput } from '@/components/molecules/MentionsInput'
import { MentionsSuggestionsBox } from '@/components/molecules/MentionsSuggestionsBox'
import { ImageEditor, imgPreview } from '@/components/organisms/ImageEditor'
import { VideoThumbnailSelector } from '@/components/organisms/VideoThumbnailSelector'
import { Main } from '@/components/templates/main'

import { useConversation } from '@/lib/hooks/useConversation'
import { useMentions } from '@/lib/hooks/useMentions'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { useVisualViewport } from '@/lib/hooks/useVisualViewport'
import { Context } from '@/lib/store'
import { ActionType } from '@/lib/store/reducer'
import { Tracking, TrackingEvent } from '@/lib/tracking'
import {
  validateMarkdownMaxLength,
  validatePreventBlankString,
} from '@/utils/formValidators'
import { getUserIdsFromMentions, isIosWebView, isNative } from '@/utils/utils'

import { UserRelationIndicatorType } from '@/enums'
import { IConversation } from '@/interfaces'

import { UgcComposerWebResult } from '@/plugins/ugc-composer'

const MAX_CAPTION_LENGTH = 2_200

export const ConversationReplyComposer: FC<RouteComponentProps> = ({
  match,
  history,
}: RouteComponentProps<{ conversationId: UUID }>) => {
  const [isImageCropLoading, setIsImageCropLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [visualViewportHeight, setVisualViewportHeight] = useState(0)
  const [crop, setCrop] = useState(null)
  const [replyMediaFile, setReplyMediaFile] = useState('')
  const [aspect, setAspect] = useState(9 / 16)
  const [selectedFile, setSelectedFile] = useState(null)
  const modal = useRef<HTMLIonModalElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const inputRef = useRef<HTMLDivElement>()
  const [isCanvasPreviewLoading, setCanvasPreviewLoading] = useState(false)
  const [presentToast] = useIonToast()
  const { dispatch } = useContext(Context)

  useEffect(() => {
    dispatch({
      type: ActionType.SET_VIDEO_PLAYBACK_ALLOWED,
      payload: false,
    })

    return () => {
      dispatch({
        type: ActionType.SET_VIDEO_PLAYBACK_ALLOWED,
        payload: true,
      })
    }
  }, [dispatch])

  const {
    params: { conversationId },
  } = match

  const onFileUploadComplete = useCallback(() => {
    presentToast({
      header: t('conversation.toastHeader'),
      message: t('conversation.toastMessage'),
      position: 'top',
      duration: 3000,
    })
  }, [presentToast]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    onCreateConversationReply,
    onGetConversation,
    conversation,
    uploadConversationMediaFile,
    isUploading,
  } = useConversation({
    onFileUploadComplete,
  })

  useEffect(() => {
    if (conversation) {
      setAspect(conversation.imageAspectRatio)
    }
  }, [conversation])

  const { t } = useTranslation('common')
  const { data: authUser } = useUserInfo()
  const {
    visualViewport: { height },
  } = useVisualViewport()
  const [squareRef, { height: commentHeight }] = useElementSize()
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    watch,
  } = useForm({
    defaultValues: {
      caption: '',
    },
  })

  const captionValue = watch('caption')

  const {
    mentionsContainerRef,
    fetchMentions,
    mentions,
    isLoadingMentions,
    mentionsCount,
    setMentionsCount,
    onBlur,
  } = useMentions()
  const shouldHaveBorder = mentions.length || isLoadingMentions

  const item = {
    id: authUser.id,
    name: authUser.username,
    isModerator: authUser?.isModerator,
    isAmbassador: authUser?.isAmbassador,
    badge: authUser?.badge,
  }

  useEffect(() => {
    const subscription = watch(
      debounce(
        (value) =>
          setMentionsCount(getUserIdsFromMentions(value.comment).length),
        500
      )
    )
    return () => subscription.unsubscribe()
  }, [watch, setMentionsCount])

  const onConversationReplySubmit = async (data: IConversation) => {
    try {
      history.goBack()
      const conversationReply = await onCreateConversationReply({
        id: conversation.id,
        ...data,
        mediaType: conversation.mediaType,
      })

      uploadConversationMediaFile(
        conversation.id + '_' + conversationReply.id,
        replyMediaFile,
        conversation.mediaType,
        data.caption
      )

      if (conversation.id && conversationReply.id) {
        Tracking.triggerEvent(TrackingEvent.AddReplyCompleted, {
          conversationId: conversation.id,
          replyId: conversationReply.id,
        })
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleModalDismiss = () => {
    setIsModalOpen(() => false)
    setAspect(() => 0)
    setSelectedFile(() => null)
  }

  const handleImageEdit = async () => {
    setIsImageCropLoading(true)
    setCanvasPreviewLoading(true)
    handleModalDismiss()
    const preview = await imgPreview(imgRef.current, crop)
    setReplyMediaFile(preview)
    setIsImageCropLoading(false)
  }

  const onFileChangeConversation = async ({ media }: UgcComposerWebResult) => {
    setSelectedFile(media?.url)
    if (isNative() || media.mediaType === 'video') {
      setReplyMediaFile(media?.url)
    } else {
      setIsModalOpen(true)
    }
  }

  const viewportStyles = isIosWebView()
    ? {
        height: visualViewportHeight ? `${visualViewportHeight}px` : '100%',
      }
    : null

  const isFitsInViewport = visualViewportHeight >= commentHeight

  const header = (
    <div className="flex justify-between items-center">
      <IonButtons slot="secondary">
        <BackButton defaultHref="/feed" />
      </IonButtons>
      <IonTitle>
        <Title level={3}>{t('conversation.reply')}</Title>
      </IonTitle>
      <Button
        size="small"
        className="min-w-[5.5rem]"
        isDisabled={!replyMediaFile || isUploading || !isValid}
        onClick={handleSubmit(onConversationReplySubmit)}
      >
        {isUploading ? <IonSpinner color="light" /> : t('common.post')}
      </Button>
    </div>
  )

  useEffect(() => {
    if (isIosWebView()) {
      setVisualViewportHeight(height)
    }
  }, [height])

  useEffect(() => {
    onGetConversation(conversationId)
    const state = history.location?.state as UgcComposerWebResult

    state?.media?.url && setReplyMediaFile(state?.media?.url)
    state?.media?.ratio && setAspect(state?.media?.ratio)
  }, [conversationId, history.location?.state, onGetConversation])

  const validationMessages = {
    required: t('required'),
    minLength: t('minLength', { length: 10 }),
    maxLength: t('maxLength', { length: MAX_CAPTION_LENGTH }),
  }

  return (
    <Main
      header={header}
      style={viewportStyles}
      headerClassName="touch-none"
      className={`h-full overflow-auto flex ${
        isFitsInViewport && isIosWebView() ? 'touch-none' : 'touch-auto'
      }`}
    >
      <div ref={squareRef}>
        <IonList className="grow-1 composer">
          <IonItem>
            <div className="flex items-center">
              <IonRouterLink
                routerLink={`/@${conversation?.username}`}
                className="flex-none mr-2 self-start"
              >
                <Avatar
                  userId={conversation?.userId}
                  isModerator={conversation?.isModerator}
                  isAmbassador={conversation?.isAmbassador}
                  badge={conversation?.badge}
                />
              </IonRouterLink>
              <div className="relative">
                <div className="m-0 cursor-pointer leading-none capitalize">
                  <IonRouterLink
                    className="cursor-pointer text-white"
                    routerLink={`/@${conversation?.username}`}
                  >
                    <strong className="break-words">
                      {conversation?.title}
                    </strong>
                  </IonRouterLink>
                </div>
                <span className="flex text-xs whitespace-nowrap">
                  <span className="inline-block mr-1">
                    {t('contest.details.createdBy')}
                  </span>
                  <strong>
                    <IonRouterLink routerLink={`/@${conversation?.username}`}>
                      <UserName
                        name={conversation?.username}
                        className="text-cool-gray-200"
                      />
                    </IonRouterLink>
                  </strong>
                </span>
              </div>
            </div>
          </IonItem>

          <IonItem>
            <span className="pl-3 mb-3 flex">
              <UserRelationIndicator
                elementType={UserRelationIndicatorType.curvedRight}
              />
            </span>
            <span className="pr-2">
              <Avatar
                username={item.name}
                size="small"
                userId={item.id}
                isModerator={item?.isModerator}
                isAmbassador={item?.isAmbassador}
                badge={item?.badge}
                clickable
              />
            </span>
            <IonRouterLink routerLink={`/@${item?.name}`}>
              <UserName className="text-sm" name={item?.name} />
            </IonRouterLink>
          </IonItem>
          <IonItem>
            <div className="w-full ml-[4.6875rem]">
              <Controller
                control={control}
                name="caption"
                rules={{
                  required: true,
                  validate: {
                    pattern: validatePreventBlankString(),
                    maxLength: validateMarkdownMaxLength(MAX_CAPTION_LENGTH),
                  },
                }}
                render={({ field }) => (
                  <MentionsInput
                    field={field}
                    maxLength={MAX_CAPTION_LENGTH}
                    mentionsContainerRef={mentionsContainerRef}
                    fetchMentions={fetchMentions}
                    mentionsCount={mentionsCount}
                    placeholder={t('UGC.conversation.form.description')}
                    onBlur={onBlur}
                  />
                )}
              />
              {errors?.caption && (
                <IonLabel
                  color="danger"
                  className="text-left block mb-1 !text-xs"
                >
                  {validationMessages[errors?.caption?.type] ??
                    errors?.caption?.message}
                </IonLabel>
              )}
              <CharacterCount
                currentInput={captionValue}
                maxLength={MAX_CAPTION_LENGTH}
              />
            </div>
          </IonItem>
          <MentionsSuggestionsBox
            className={shouldHaveBorder && `border-y-2 border-y-cool-gray-700`}
            suggestionsPortalHostRef={mentionsContainerRef}
            isLoadingMentions={isLoadingMentions}
          />
        </IonList>
        <section className="pl-[5.625rem]">
          {!replyMediaFile ? (
            <div
              ref={inputRef}
              className="pt-5 mr-2.5 overflow-x-scroll grid gap-4 grid-flow-col auto-cols-[minmax(8.75rem,_10.875rem)]"
            >
              <AddImageButton
                isLoading={isCanvasPreviewLoading}
                onFileChange={onFileChangeConversation}
                contentType="reply"
                mediaType={conversation?.mediaType || 'image'}
                title={conversation?.title}
              />
            </div>
          ) : (
            <div className="pt-5 relative inline-block">
              <button
                onClick={() => {
                  setReplyMediaFile(null)
                  setCanvasPreviewLoading(false)
                }}
                className="z-1 top-3 -right-2 bg-white rounded-full w-5 h-5 text-black flex justify-center items-center float-right absolute"
              >
                <IonIcon icon={closeOutline} className="w-4 h-4" />
              </button>
              {conversation?.mediaType === 'image' ? (
                <img width="174" src={replyMediaFile} />
              ) : (
                <div className="flex-1 relative -z-10 w-44">
                  <VideoThumbnailSelector
                    videoUrl={replyMediaFile}
                    thumbnailUrl={null}
                  />
                </div>
              )}
            </div>
          )}
          <img src={selectedFile} ref={imgRef} className="opacity-0" />
        </section>
      </div>
      <IonModal
        ref={modal}
        isOpen={isModalOpen}
        breakpoints={[0, 1]}
        handle={false}
        animated
        canDismiss
        showBackdrop
        onDidDismiss={handleModalDismiss}
      >
        <IonHeader className="mx-auto max-w-[40rem]">
          <IonToolbar color="dark" mode="ios">
            <div className="flex justify-between items-center">
              <Button onClick={handleModalDismiss} className="plain -ml-3">
                <IonIcon
                  className="text-primary"
                  slot="icon-only"
                  icon={closeOutline}
                />
              </Button>
              <IonTitle>
                <Title level={3}>{t('UGC.editor.title')}</Title>
              </IonTitle>
              <Button
                size="small"
                className="min-w-[5.5rem]"
                onClick={handleImageEdit}
                isDisabled={isImageCropLoading}
              >
                {isImageCropLoading ? (
                  <IonSpinner color="light" />
                ) : (
                  t('UGC.editor.crop')
                )}
              </Button>
            </div>
          </IonToolbar>
        </IonHeader>
        <IonContent color="dark">
          <div className="h-full w-full flex items-center">
            <ImageEditor
              image={selectedFile}
              aspect={aspect}
              followAspect
              onCrop={(c) => setCrop(c)}
            />
          </div>
        </IonContent>
      </IonModal>
    </Main>
  )
}
